import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, API, TOKEN_KEY } from "@/lib/api";

const CmsContext = createContext(null);

/**
 * PART 8 — CMS provider.
 *  • Loads the map of admin-editable copy overrides ({key: value}) app-wide.
 *  • Opens a realtime WebSocket so edits made by any admin propagate to every
 *    open session immediately. Falls back to a refetch on window focus.
 *  • Exposes `t(key, fallback)` so any component can render editable copy.
 */
export function CmsProvider({ children }) {
  const [content, setContent] = useState({});
  const [ready, setReady] = useState(false);
  const wsRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const d = await api.cmsContent();
      setContent(d || {});
    } catch {
      /* keep whatever we have */
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Realtime channel — reconnects automatically.
  useEffect(() => {
    let stopped = false;
    let retry = null;
    const connect = () => {
      if (stopped) return;
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const base = String(API || "").replace(/^http/, "ws"); // https->wss, http->ws
        const url = `${base}/ws${token ? `?token=${encodeURIComponent(token)}` : ""}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          let msg = null;
          try { msg = JSON.parse(ev.data); } catch { return; }
          if (!msg || !msg.type) return;
          if (msg.type === "cms:update") refresh();
          // Let data-driven pages know something changed so they can refetch.
          window.dispatchEvent(new CustomEvent("realtime:update", { detail: msg }));
        };
        ws.onclose = () => { if (!stopped) retry = setTimeout(connect, 4000); };
        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      } catch {
        if (!stopped) retry = setTimeout(connect, 4000);
      }
    };
    connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
    };
  }, [refresh]);

  // Fallback: re-sync when the tab regains focus.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const t = useCallback((key, fallback = "") => {
    const v = content[key];
    return (v === undefined || v === null || v === "") ? fallback : v;
  }, [content]);

  const setLocal = useCallback((key, value) => {
    setContent((c) => ({ ...c, [key]: value }));
  }, []);

  return (
    <CmsContext.Provider value={{ content, t, refresh, setLocal, ready }}>
      {children}
    </CmsContext.Provider>
  );
}

export function useCms() {
  const ctx = useContext(CmsContext);
  if (!ctx) {
    // Safe no-op fallback if used outside the provider.
    return { content: {}, t: (_k, f = "") => f, refresh: () => {}, setLocal: () => {}, ready: true };
  }
  return ctx;
}
